/**
 * PeekManager Context and Hook
 * Phase 8: Peek Surfaces - State Management
 * 
 * Provides centralized state management for peek panels.
 * Ensures only one peek panel can be open at a time.
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type PeekId = 'ai-chat' | 'quick-note' | string;

export interface PeekState {
  /** Currently open peek panel ID */
  openPeekId: PeekId | null;
  /** Width of the currently open peek panel */
  peekWidth: number;
}

export interface PeekContextValue {
  /** Current peek state */
  state: PeekState;
  /** Open a peek panel by ID */
  openPeek: (id: PeekId) => void;
  /** Close the current peek panel */
  closePeek: () => void;
  /** Toggle a peek panel (open if closed, close if open) */
  togglePeek: (id: PeekId) => void;
  /** Check if a specific peek panel is open */
  isPeekOpen: (id: PeekId) => boolean;
  /** Update the peek panel width */
  updatePeekWidth: (width: number) => void;
}

/**
 * Peek Context
 */
const PeekContext = createContext<PeekContextValue | undefined>(undefined);

/**
 * PeekManager Provider Component
 * 
 * Provides peek state management to all child components
 */
export const PeekManagerProvider: React.FC<{
  children: React.ReactNode;
  initialPeekId?: PeekId | null;
  initialWidth?: number;
}> = ({ children, initialPeekId = null, initialWidth = 320 }) => {
  const [state, setState] = useState<PeekState>({
    openPeekId: initialPeekId,
    peekWidth: initialWidth,
  });

  /**
   * Open a peek panel
   */
  const openPeek = useCallback((id: PeekId) => {
    setState(prev => ({
      ...prev,
      openPeekId: id,
    }));
  }, []);

  /**
   * Close the current peek panel
   */
  const closePeek = useCallback(() => {
    setState(prev => ({
      ...prev,
      openPeekId: null,
    }));
  }, []);

  /**
   * Toggle a peek panel
   */
  const togglePeek = useCallback((id: PeekId) => {
    setState(prev => ({
      ...prev,
      openPeekId: prev.openPeekId === id ? null : id,
    }));
  }, []);

  /**
   * Check if a specific peek panel is open
   */
  const isPeekOpen = useCallback((id: PeekId) => {
    return state.openPeekId === id;
  }, [state.openPeekId]);

  /**
   * Update peek width
   */
  const updatePeekWidth = useCallback((width: number) => {
    setState(prev => ({
      ...prev,
      peekWidth: width,
    }));
  }, []);

  /**
   * Handle Escape key to close peek
   */
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && state.openPeekId) {
        event.preventDefault();
        closePeek();
      }
    };

    document.addEventListener('keydown', handleEscapeKey);
    return () => document.removeEventListener('keydown', handleEscapeKey);
  }, [state.openPeekId, closePeek]);

  const value: PeekContextValue = {
    state,
    openPeek,
    closePeek,
    togglePeek,
    isPeekOpen,
    updatePeekWidth,
  };

  return (
    <PeekContext.Provider value={value}>
      {children}
    </PeekContext.Provider>
  );
};

/**
 * usePeek Hook
 * 
 * Provides access to peek state and management functions
 */
export const usePeek = (): PeekContextValue => {
  const context = useContext(PeekContext);

  if (context === undefined) {
    throw new Error('usePeek must be used within a PeekManagerProvider');
  }

  return context;
};

/**
 * usePeekOpen Hook
 * 
 * Convenience hook to check if a specific peek is open
 */
export const usePeekOpen = (id: PeekId): boolean => {
  const { isPeekOpen } = usePeek();
  return isPeekOpen(id);
};

/**
 * usePeekControl Hook
 * 
 * Convenience hook to control a specific peek
 */
export const usePeekControl = (id: PeekId) => {
  const { openPeek, closePeek, togglePeek, isPeekOpen } = usePeek();
  
  return {
    isOpen: isPeekOpen(id),
    open: () => openPeek(id),
    close: closePeek,
    toggle: () => togglePeek(id),
  };
};
